"use client";

import styles from "./footer.module.css";
import Contact from "./contact";
import { useResponsive } from "@/hooks/useResponsive";
import Link from "next/link";
import React from "react";
import { useParams, usePathname } from "next/navigation";
// import defaultFooterData from "@/data/footerData.json";
import { Description } from "@/components/Common/Typography";
import { FooterData } from "@/types/footer";
import { useProjectInfo } from "@/context/ProjectInfoContext";

interface FooterProps {
  forceStatic?: boolean;
  data?: FooterData;
}

export default function Footer({
  forceStatic = false,
  data: propData,
}: FooterProps) {
  const pathname = usePathname();
  const params = useParams();
  const locale = ((params?.locale as string) || "vi") as "vi" | "en";

  const { isMobile, isTablet, isDesktop } = useResponsive();
  const { projectInfo } = useProjectInfo();

  // Use passed data prop if provided, otherwise fallback to global projectInfo
  const currentData = propData || projectInfo || {};

  // Helper to extract localized or flat single-language data
  const getLocalized = (obj: any) => {
    if (!obj) return undefined;
    if (obj[locale]) return obj[locale];
    if (obj.vi) return obj.vi;
    return obj; // Flat object
  };

  const getLocalizedTitle = (titleOrObj: any) => {
    if (!titleOrObj) return "";
    if (typeof titleOrObj === "string") return titleOrObj;
    return titleOrObj[locale] || titleOrObj.vi || "";
  };

  // Hide the layout footer on homepage to avoid duplication with the full-page section
  const isHome =
    pathname === `/${locale}` ||
    pathname === `/${locale}/` ||
    pathname === "/" ||
    pathname === "";
  if (isHome && !forceStatic) return null;

  const isFullPageStyle = isDesktop || !isMobile;
  const rootClass = `${styles.footer_root} ${
    isFullPageStyle ? styles.footer_fullpage : styles.footer_static
  }`;

  const company = getLocalized(currentData?.company);
  const policies = getLocalized(currentData?.policies);
  const factories = getLocalized(currentData?.factories);
  const socials = currentData?.socials;
  const socialsTitle = getLocalizedTitle(socials?.title);
  const contactFormTitle = getLocalizedTitle(currentData?.contactForm?.title);
  const cert = currentData?.certification;
  const certInfo = getLocalized(cert);

  const facebookUrl =
    socials?.facebook ||
    socials?.items?.find((i: any) => i.platform === "facebook")?.url;
  const youtubeUrl =
    socials?.youtube ||
    socials?.items?.find((i: any) => i.platform === "youtube")?.url;
  const tiktokUrl =
    socials?.tiktok ||
    socials?.items?.find((i: any) => i.platform === "tiktok")?.url;
  const zaloUrl =
    socials?.zalo ||
    socials?.items?.find((i: any) => i.platform === "zalo")?.url;

  return (
    <footer className={rootClass}>
      <div className={styles.footer_container}>
        {/* LEFT COLUMN */}
        <div style={{ padding: "4px", zIndex: 2 }}>
          {/* ROW 1: Logo & Company Info */}
          <div
            style={{
              borderBottom: isMobile
                ? "1px solid rgba(255, 255, 255, 0.3)"
                : "none",
              marginBottom: isMobile ? "20px" : "25px",
              paddingBottom: isMobile ? "20px" : "0",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "28px",
              }}
            >
              <div style={{ flex: isMobile ? "1" : "0 0 28%" }}>
                <img
                  src="/logos/logo_white.png"
                  alt="Logo"
                  className={styles.footer_logo}
                />
              </div>
              <div
                style={{
                  flex: isMobile ? "1" : "1",
                  paddingLeft: isMobile ? 0 : "15px",
                }}
              >
                <div className={styles.footer_company_name}>
                  {company?.name}
                </div>
                <div style={{ marginTop: "15px" }}>
                  {company?.address && (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      <img
                        src="/icons/icon_home.svg"
                        width={18}
                        alt="Address"
                      />
                      <Description
                        size="small"
                        color="#fff"
                        className={styles.footer_text}
                      >
                        {company.address}
                      </Description>
                    </div>
                  )}
                  {company?.email && (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      <img src="/icons/icon_email.svg" width={18} alt="Email" />
                      <Description
                        size="small"
                        color="#fff"
                        className={styles.footer_text}
                      >
                        {company.email}
                      </Description>
                    </div>
                  )}
                  {company?.phone && (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      <img src="/icons/icon_phone.svg" width={18} alt="Phone" />
                      <Description
                        size="small"
                        color="#fff"
                        className={styles.footer_text}
                      >
                        {company.phone}
                      </Description>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ROW 2: Policies & Factories */}
          <div
            style={{
              borderBottom: isMobile
                ? "1px solid rgba(255, 255, 255, 0.3)"
                : "none",
              marginBottom: isMobile ? "20px" : "0",
              paddingBottom: isMobile ? "20px" : "0",
              borderTop: !isMobile
                ? "1px solid rgba(255, 255, 255, 0.2)"
                : "none",
              paddingTop: !isMobile ? "30px" : "0",
            }}
          >
            <div
              style={{
                display: isMobile ? "block" : "grid",
                gridTemplateColumns: "1fr 1.5fr",
                gap: "1.5rem",
              }}
            >
              {/* Policies List */}
              <div style={{ marginBottom: isMobile ? "20px" : "0" }}>
                <div className={styles.footer_title}>{policies?.title}</div>
                <div>
                  {policies?.items?.map((item: any, k: number) => (
                    <Link
                      scroll={false}
                      href={item.url || `/chinh-sach/${item.slug}`}
                      key={k}
                    >
                      <Description
                        size="small"
                        color="#fff"
                        className={styles.footer_link}
                      >
                        {item.title}
                      </Description>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Factories List */}
              <div>
                <div className={styles.footer_title}>{factories?.title}</div>
                {factories?.items?.map((item: any, k: number) => (
                  <Description
                    size="small"
                    color="#fff"
                    className={styles.footer_text}
                    key={k}
                  >
                    {item.name ? `${item.name}: ${item.address}` : item.address}
                  </Description>
                ))}
              </div>
            </div>
          </div>

          {/* ROW 3: Copyright & Badges (Desktop) */}
          {!isMobile && (
            <div
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "center",
                marginTop: "30px",
              }}
            >
              {cert?.badgeUrl && (
                <a
                  href={cert.badgeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={"/logos/bct.png"}
                    width={110}
                    alt={cert.badgeAlt || "Bộ Công Thương"}
                    style={{ marginTop: 5 }}
                  />
                </a>
              )}
              <div style={{ textAlign: "left" }}>
                {certInfo?.info1 && (
                  <Description
                    size="small"
                    color="rgba(255, 255, 255, 0.85)"
                    className={styles.footer_bottom_text}
                  >
                    {certInfo.info1}
                  </Description>
                )}
                {certInfo?.info2 && (
                  <Description
                    size="small"
                    color="rgba(255, 255, 255, 0.85)"
                    className={styles.footer_bottom_text}
                  >
                    {certInfo.info2}
                  </Description>
                )}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Social & Contact Form */}
        <div
          style={{
            flex: isMobile ? "0 0 100%" : "1",
            padding: "4px",
            zIndex: 2,
          }}
        >
          {/* Social icons */}
          <div className={styles.footer_title}>{socialsTitle}</div>
          <div
            style={{
              display: "flex",
              gap: "15px",
              marginTop: "15px",
              justifyContent: "flex-start",
            }}
          >
            <SocialIcon
              href={facebookUrl}
              icon="/icons/fb_icon_white.svg"
              label="Facebook"
            />
            <SocialIcon
              href={youtubeUrl}
              icon="/icons/youtube_icon_white.svg"
              label="YouTube"
            />
            <SocialIcon
              href={tiktokUrl}
              icon="/icons/tiktok_icon_white.svg"
              label="TikTok"
            />
            <SocialIcon
              href={zaloUrl}
              icon="/icons/zalo_icon.svg"
              label="Zalo"
            />
          </div>

          {/* Contact form */}
          <div className={styles.footer_title} style={{ marginTop: "30px" }}>
            {contactFormTitle}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              width: "100%",
            }}
          >
            <Contact
              fontSize={isTablet ? "14px" : isMobile ? "12px" : "15px"}
            />
          </div>

          {/* ROW 3: Copyright & Badges (Mobile) */}
          {isMobile && (
            <div
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "center",
                marginTop: "30px",
                borderTop: "1px solid rgba(255, 255, 255, 0.3)",
                paddingTop: "20px",
              }}
            >
              {cert?.badgeUrl && (
                <a
                  href={cert.badgeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={"/logos/bct.png"}
                    width={110}
                    alt={cert.badgeAlt || "Bộ Công Thương"}
                  />
                </a>
              )}
              <div style={{ textAlign: "left" }}>
                {certInfo?.info1 && (
                  <Description
                    size="small"
                    color="rgba(255, 255, 255, 0.85)"
                    className={styles.footer_bottom_text}
                  >
                    {certInfo.info1}
                  </Description>
                )}
                {certInfo?.info2 && (
                  <Description
                    size="small"
                    color="rgba(255, 255, 255, 0.85)"
                    className={styles.footer_bottom_text}
                  >
                    {certInfo.info2}
                  </Description>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <DecoImages isMobile={isMobile} isHome={isHome} />
    </footer>
  );
}

function DecoImages({ isMobile, isHome }: any) {
  const decoImages = [
    {
      src: "/images/cafe_deco.png",
      style: {
        bottom: 0,
        left: isMobile ? "-2vw" : 0,
      },

      size: {
        mobile: { w: 100, h: 100 },
        desktop: { w: 220, h: 220 },
      },
    },
    {
      src: "/images/cafe_deco_2.png",
      style: { bottom: 0, right: 0 },

      size: {
        mobile: { w: 150, h: 150 },
        desktop: { w: 260, h: 260 },
      },
    },
    {
      src: "/images/cafe_deco_3.png",
      style: {
        bottom: 0,
        left: isMobile ? "15vw" : "18%",
      },

      size: {
        mobile: { w: 120, h: 40 },
        desktop: { w: 420, h: 130 },
      },
    },
    {
      src: "/images/tea_deco_3.png",
      style: {
        bottom: 0,
        right: isMobile ? "15vw" : "15%",
      },
      size: {
        mobile: { w: 100, h: 100 },
        desktop: { w: 220, h: 220 },
      },
    },
    {
      src: "/images/tea_deco_4.png",
      style: { top: 50, right: 0 },
      size: {
        mobile: { w: 100, h: 100 },
        desktop: { w: 180, h: 180 },
      },
    },
  ];

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {decoImages.map((img, i) => {
        const size = isMobile ? img.size.mobile : img.size.desktop;

        return (
          <img
            key={i}
            src={img.src}
            style={{
              position: "absolute",
              width: size.w,
              height: size.h,
              ...img.style,
            }}
          />
        );
      })}
    </div>
  );
}

function SocialIcon({
  href,
  icon,
  label,
}: {
  href?: string;
  icon: string;
  label: string;
}) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
      <img src={icon} width={32} alt={label} />
    </a>
  );
}
